import type { FunctionComponent, MouseEventHandler } from "react"

import { useParams } from "next/navigation"

import { Button } from "@jobteaser/spark/components/Button"
import { Plus } from "@jobteaser/spark/components/Icons/Plus"

import { EXTERNAL, JOBTEASER, WITHOUT_REGISTRATION } from "@/modules/events/constants"
import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { TrackRender } from "@/modules/tracking/components/TrackRender"
import { ensureTrackingProps } from "@/modules/tracking/helpers/ensureTrackingProps"
import { useTrackEvent } from "@/modules/tracking/useTrackEvent"

type WaitingListButtonProps = {
  handleClick: MouseEventHandler<HTMLButtonElement>
}

export const WaitingListButton: FunctionComponent<WaitingListButtonProps> = ({ handleClick }) => {
  const locale = useLocale()
  const { t } = useTranslation(["event_details"])
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)
  const trackEvent = useTrackEvent()

  if (!event) {
    return null
  }

  const { registrationDetails, id } = event

  const { type } = registrationDetails
  const isEventWithoutRegistration = type === WITHOUT_REGISTRATION
  const isExternalEvent = type === EXTERNAL
  const isJobteaserRegistration = type === JOBTEASER

  if (isExternalEvent || isEventWithoutRegistration) {
    return null
  }

  const hostname = typeof window !== "undefined" ? window.location.host : ""
  const [subdomain] = hostname.split(".")

  const onClick: MouseEventHandler<HTMLButtonElement> = evt => {
    trackEvent("event_ad_subscription_click", {
      context: "event_ad",
      event_ad_id: id,
      is_registering_on_waiting_list: true, // additionnal properties are allowed in the schema
      service: "event_details",
      trackee_locale: locale,
    })

    if (isJobteaserRegistration) {
      handleClick(evt)
    }
  }

  return (
    <>
      <TrackRender
        trackingProps={ensureTrackingProps({
          eventName: "event_ad_subscribe_display",
          eventProperties: {
            event_id: event.id,
            is_registering_on_waiting_list: true, // additionnal properties are allowed in the schema
            is_visible: true,
            service: "event_details",
            subdomain,
          },
        })}
      />
      <Button
        data-testid="event-details-registration-details-subscribe-button"
        icon={Plus}
        onClick={onClick}
        variant="secondary"
      >
        {t("event_details.subscribe_to_waiting_list")}
      </Button>
    </>
  )
}
